/**
 * Orders this browser has placed. The Data Platform can read a booked order
 * back as Submitted for a while after Place order, and the same order can be
 * opened in another tab, so the status alone cannot always tell a placed order
 * from a draft (CLOS-14645). Kept in local storage for a day: long enough to
 * outlast the read delay and a stale tab, short enough not to pile up.
 */
const STORAGE_KEY = 'clinigen.opaCheckout.placedOrders'
const MAX_REMEMBERED_ORDERS = 50
const REMEMBER_FOR_MS = 24 * 60 * 60 * 1000

interface PlacedOrder {
  id: string
  at: number
}

const isPlacedOrder = (value: unknown): value is PlacedOrder =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PlacedOrder).id === 'string' &&
  typeof (value as PlacedOrder).at === 'number'

const read = (now: number): PlacedOrder[] => {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) ?? '[]'
    )
    return Array.isArray(parsed)
      ? parsed
          .filter(isPlacedOrder)
          .filter((order) => now - order.at < REMEMBER_FOR_MS)
      : []
  } catch {
    return []
  }
}

export const rememberPlacedOrder = (orderId?: string | number): void => {
  if (orderId === undefined || orderId === null || orderId === '') return
  const id = String(orderId)
  const now = Date.now()
  try {
    const orders = read(now).filter((existing) => existing.id !== id)
    orders.push({ id, at: now })
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(orders.slice(-MAX_REMEMBERED_ORDERS))
    )
  } catch {
    // Storage can be unavailable; the status check still applies.
  }
}

export const wasOrderPlacedRecently = (orderId?: string | number): boolean =>
  orderId !== undefined &&
  orderId !== null &&
  read(Date.now()).some((order) => order.id === String(orderId))
